import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import StepWizard from '../../components/common/StepWizard';
import { useAppContext } from '../../context/AppContext';

const Step1BasicInfo = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Basic Information
    </Typography>
    <TextField
      fullWidth
      label="Channel Name"
      value={data.name || ''}
      onChange={(e) => onDataChange({ name: e.target.value })}
      margin="normal"
      required
    />
    <TextField
      fullWidth
      label="Channel Description"
      value={data.description || ''}
      onChange={(e) => onDataChange({ description: e.target.value })}
      margin="normal"
      multiline
      rows={4}
      placeholder="Describe the purpose and features of this Gemini shopping channel..."
    />
  </Box>
);

const Step2Products = ({ data, onDataChange }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];

  const handleToggle = (categoryId) => {
    const currentIndex = selectedCategories.indexOf(categoryId);
    const newSelected = [...selectedCategories];

    if (currentIndex === -1) {
      newSelected.push(categoryId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onDataChange({ selectedCategories: newSelected });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Product Categories
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Select product categories to display in Google Gemini
      </Typography>
      <FormGroup sx={{ mt: 2 }}>
        {productCategories.map((category) => (
          <FormControlLabel
            key={category.id}
            control={
              <Checkbox
                checked={selectedCategories.indexOf(category.id) !== -1}
                onChange={() => handleToggle(category.id)}
              />
            }
            label={`${category.name} (${category.productCount} products)`}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

const Step3Payment = ({ data, onDataChange }) => (
  <Box>
    <Typography variant="h6" gutterBottom>
      Payment Configuration
    </Typography>
    <TextField
      fullWidth
      label="Payment Gateway"
      value={data.paymentGateway || ''}
      onChange={(e) => onDataChange({ paymentGateway: e.target.value })}
      margin="normal"
      placeholder="e.g., Google Pay, Stripe"
    />
    <TextField
      fullWidth
      label="API Key"
      value={data.apiKey || ''}
      onChange={(e) => onDataChange({ apiKey: e.target.value })}
      margin="normal"
      type="password"
      placeholder="Enter payment gateway API key"
    />
    <TextField
      fullWidth
      label="Callback URL"
      value={data.callbackUrl || ''}
      onChange={(e) => onDataChange({ callbackUrl: e.target.value })}
      margin="normal"
      placeholder="https://your-domain.com/callback"
    />
  </Box>
);

const Step4Review = ({ data }) => {
  const { productCategories } = useAppContext();
  const selectedCategories = data.selectedCategories || [];
  const selectedCategoryNames = productCategories
    .filter(cat => selectedCategories.includes(cat.id))
    .map(cat => cat.name);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Configuration
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">Channel Name</Typography>
        <Typography variant="body1" gutterBottom>{data.name || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Channel Description</Typography>
        <Typography variant="body1" gutterBottom>{data.description || 'Not set'}</Typography>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Selected Product Categories</Typography>
        <List dense>
          {selectedCategoryNames.length > 0 ? (
            selectedCategoryNames.map((name, index) => (
              <ListItem key={index}>
                <ListItemText primary={name} />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">No categories selected</Typography>
          )}
        </List>

        <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2 }}>Payment Gateway</Typography>
        <Typography variant="body1" gutterBottom>{data.paymentGateway || 'Not set'}</Typography>
      </Box>
    </Box>
  );
};

const GeminiWizard = () => {
  const { addSalesChannel } = useAppContext();
  const navigate = useNavigate();

  const steps = [
    { label: 'Basic Information', component: <Step1BasicInfo /> },
    { label: 'Select Products', component: <Step2Products /> },
    { label: 'Payment Configuration', component: <Step3Payment /> },
    { label: 'Review & Create', component: <Step4Review /> }
  ];

  const handleComplete = (formData) => {
    const channel = {
      name: formData.name,
      type: 'gemini',
      typeName: 'Google Gemini',
      config: formData
    };
    addSalesChannel(channel);
    navigate('/sales-channels');
  };

  return <StepWizard steps={steps} onComplete={handleComplete} title="Create Google Gemini Shopping Channel" />;
};

export default GeminiWizard;
